import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, DollarSign, TrendingUp, AlertOctagon, CheckCircle2, Receipt, Paperclip, FileText, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";

const STATUSES = ["Open", "Paid", "Overdue", "Cancelled"];

const STATUS_COLORS = {
  Open: "bg-blue-100 text-blue-700",
  Paid: "bg-emerald-100 text-emerald-700",
  Overdue: "bg-rose-100 text-rose-700",
  Cancelled: "bg-slate-100 text-slate-500",
};

const blankExpense = {
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: "",
  invoice_no: "",
  status: "Open",
  category: "",
  pay_to: "",
  amount: "",
  property_id: "",
  paid_date: "",
  description: "",
  receipt_file_ids: [],
};

const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FinancialsPage() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankExpense);
  const [editingId, setEditingId] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptCache, setReceiptCache] = useState({}); // id -> {filename, content_type}

  const load = async () => {
    const [e, p, m, s] = await Promise.all([
      api.get("/expenses"),
      api.get("/properties"),
      api.get("/expenses/meta"),
      api.get("/expenses/stats/summary"),
    ]);
    setItems(e.data); setProperties(p.data); setCategories(m.data.categories); setStats(s.data);
  };
  useEffect(() => { load(); }, []);

  const onField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditingId(null); setForm(blankExpense); setOpen(true); };
  const openEdit = (exp) => {
    setEditingId(exp.id);
    setForm({
      invoice_date: exp.invoice_date || "",
      due_date: exp.due_date || "",
      invoice_no: exp.invoice_no || "",
      status: exp.status || "Open",
      category: exp.category || "",
      pay_to: exp.pay_to || "",
      amount: exp.amount ?? "",
      property_id: exp.property_id || "",
      paid_date: exp.paid_date || "",
      description: exp.description || "",
      receipt_file_ids: exp.receipt_file_ids || [],
    });
    // load receipt metadata for display
    if (exp.receipt_file_ids?.length) {
      api.get("/files?category=receipt").then(r => {
        const map = {};
        for (const f of r.data) map[f.id] = f;
        setReceiptCache(prev => ({ ...prev, ...map }));
      });
    }
    setOpen(true);
  };

  const uploadReceipt = async (file) => {
    setUploadingReceipt(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "receipt");
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptCache(prev => ({ ...prev, [data.id]: data }));
      setForm(f => ({ ...f, receipt_file_ids: [...(f.receipt_file_ids || []), data.id] }));
      toast.success("Receipt attached");
    } catch (e) { toast.error("Upload failed"); }
    setUploadingReceipt(false);
  };

  const removeReceipt = (fileId) => {
    setForm(f => ({ ...f, receipt_file_ids: (f.receipt_file_ids || []).filter(id => id !== fileId) }));
  };

  const downloadReceipt = async (fileId) => {
    const meta = receiptCache[fileId];
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = meta?.original_filename || `receipt-${fileId}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.property_id || payload.property_id === "none") delete payload.property_id;
      if (editingId) {
        await api.patch(`/expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setOpen(false); setForm(blankExpense); setEditingId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Deleted"); load();
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/expenses/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url; a.download = `rojasproperty-expenses-${today}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} expense${items.length !== 1 ? "s" : ""}`);
    } catch { toast.error("Export failed"); }
  };

  const markPaid = async (exp) => {
    try {
      await api.patch(`/expenses/${exp.id}`, { status: "Paid", paid_date: new Date().toISOString().slice(0, 10) });
      toast.success("Marked as paid"); load();
    } catch { toast.error("Update failed"); }
  };

  const filtered = useMemo(() => items.filter(i => {
    if (filterStatus !== "all" && i.effective_status !== filterStatus) return false;
    if (filterCategory !== "all" && i.category !== filterCategory) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${i.invoice_no} ${i.pay_to} ${i.category} ${i.description} ${i.property_name || ""}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [items, filterStatus, filterCategory, search]);

  const filteredTotal = filtered.reduce((s, i) => s + (i.effective_status === "Cancelled" ? 0 : Number(i.amount || 0)), 0);

  return (
    <AppLayout
      title="Financials"
      subtitle="Track business expenses, vendor invoices & cash flow"
      actions={
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/expenses/export.csv"
            filenameBase="rojasproperty-expenses"
            testidPrefix="export-expenses"
            disabled={items.length === 0}
          />
          <Button data-testid="add-expense-button" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 gap-2">
            <Plus className="h-4 w-4" /> New Expense
          </Button>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-testid="financial-stats">
        <StatCard icon={DollarSign} label="Total Expenses" value={currency(stats?.total_expenses || 0)} hint={`${stats?.count ?? 0} records`} testid="stat-total" />
        <StatCard icon={CheckCircle2} label="Paid" value={currency(stats?.by_status?.Paid || 0)} hint="settled" tone="emerald" testid="stat-paid" />
        <StatCard icon={Receipt} label="Open" value={currency(stats?.by_status?.Open || 0)} hint="awaiting payment" tone="blue" testid="stat-open" />
        <StatCard icon={AlertOctagon} label="Overdue" value={currency(stats?.by_status?.Overdue || 0)} hint="past due" tone="rose" testid="stat-overdue" />
      </div>

      {/* Top categories sidebar */}
      {stats && stats.by_category?.length > 0 && (
        <Card className="p-6 border-slate-200 mb-6" data-testid="top-categories">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-slate-500" /><h3 className="font-display font-semibold">Spend by category</h3></div>
            <span className="text-xs text-slate-500">Top {Math.min(5, stats.by_category.length)}</span>
          </div>
          <div className="space-y-2.5">
            {stats.by_category.slice(0, 5).map(c => {
              const pct = stats.total_expenses ? (c.amount / stats.total_expenses) * 100 : 0;
              return (
                <div key={c.category}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-700 font-medium truncate">{c.category}</span>
                    <span className="font-mono-num text-slate-600">{currency(c.amount)} <span className="text-slate-400">· {pct.toFixed(1)}%</span></span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-900 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Filters + table */}
      <Card className="border-slate-200">
        <div className="p-5 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <h3 className="font-display font-semibold mr-auto">Expenses ({filtered.length}) <span className="text-slate-400 font-normal text-sm ml-2 font-mono-num">{currency(filteredTotal)}</span></h3>
          <div className="relative w-56">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="expense-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="w-36 h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger data-testid="filter-category" className="w-56 h-9"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Date</TableHead>
              <TableHead>Invoice No.</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Pay To</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">
                {items.length === 0 ? "No expenses yet — click \"New Expense\" to add your first." : "No matches"}
              </TableCell></TableRow>
            ) : filtered.map(e => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-slate-50">
                <TableCell className="font-mono-num text-sm">{e.invoice_date}</TableCell>
                <TableCell className="font-mono text-xs text-slate-700">{e.invoice_no}</TableCell>
                <TableCell className="font-mono-num text-sm text-slate-600">{e.due_date || "—"}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[e.effective_status] || "bg-slate-100"}`}>{e.effective_status}</span></TableCell>
                <TableCell className="text-sm text-slate-700 max-w-[200px] truncate" title={e.category}>{e.category}</TableCell>
                <TableCell className="text-sm">{e.pay_to}</TableCell>
                <TableCell className="font-mono-num text-right font-medium">{currency(e.amount)}</TableCell>
                <TableCell className="text-right">
                  {e.effective_status !== "Paid" && e.effective_status !== "Cancelled" && (
                    <button onClick={() => markPaid(e)} data-testid={`mark-paid-expense-${e.id}`} className="text-emerald-600 hover:text-emerald-700 text-xs font-medium mr-3">Mark paid</button>
                  )}
                  <button onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`} className="text-slate-400 hover:text-slate-900 mr-2"><Pencil className="h-4 w-4 inline" /></button>
                  <button onClick={() => del(e.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4 inline" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(blankExpense); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit expense" : "New expense"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4 pt-2">
            <Field label="Invoice Date *"><Input data-testid="expense-invoice-date" type="date" required value={form.invoice_date} onChange={e => onField("invoice_date", e.target.value)} /></Field>
            <Field label="Invoice No. *"><Input data-testid="expense-invoice-no" required value={form.invoice_no} onChange={e => onField("invoice_no", e.target.value)} placeholder="INV-2026-001" /></Field>
            <Field label="Due Date"><Input type="date" value={form.due_date} onChange={e => onField("due_date", e.target.value)} /></Field>
            <Field label="Paid Date"><Input type="date" value={form.paid_date} onChange={e => onField("paid_date", e.target.value)} /></Field>
            <Field label="Status *">
              <Select value={form.status} onValueChange={(v) => onField("status", v)}>
                <SelectTrigger data-testid="expense-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category *">
              <Select value={form.category} onValueChange={(v) => onField("category", v)}>
                <SelectTrigger data-testid="expense-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Pay To *" col><Input data-testid="expense-pay-to" required value={form.pay_to} onChange={e => onField("pay_to", e.target.value)} placeholder="Vendor / payee name" /></Field>
            <Field label="Amount ($) *"><Input data-testid="expense-amount" type="number" step="0.01" min="0" required value={form.amount} onChange={e => onField("amount", e.target.value)} /></Field>
            <Field label="Property (optional)">
              <Select value={form.property_id || "none"} onValueChange={(v) => onField("property_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="expense-property"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None / Business-wide —</SelectItem>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Description / Notes" col><Textarea rows={3} value={form.description} onChange={e => onField("description", e.target.value)} placeholder="Anything worth remembering for tax season…" /></Field>

            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-slate-700 flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Receipts</Label>
              <div className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50/50" data-testid="receipts-zone">
                {(form.receipt_file_ids || []).length > 0 && (
                  <ul className="space-y-1.5 mb-2">
                    {form.receipt_file_ids.map(id => {
                      const meta = receiptCache[id];
                      const isImg = meta?.content_type?.startsWith("image/");
                      return (
                        <li key={id} data-testid={`receipt-item-${id}`} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-md">
                          {isImg ? <ImageIcon className="h-4 w-4 text-slate-500" /> : <FileText className="h-4 w-4 text-slate-500" />}
                          <button type="button" onClick={() => downloadReceipt(id)} className="text-sm text-slate-700 hover:text-slate-900 truncate flex-1 text-left">
                            {meta?.original_filename || `Receipt ${id.slice(0, 8)}`}
                          </button>
                          <button type="button" onClick={() => removeReceipt(id)} className="text-slate-400 hover:text-rose-600" data-testid={`remove-receipt-${id}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    hidden
                    data-testid="receipt-upload-input"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadReceipt(f); e.target.value = ""; } }}
                  />
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 transition-colors">
                    <Paperclip className="h-3.5 w-3.5" /> {uploadingReceipt ? "Uploading…" : "Attach receipt (image or PDF)"}
                  </span>
                </label>
              </div>
            </div>

            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditingId(null); }}>Cancel</Button>
              <Button data-testid="expense-submit" type="submit" className="bg-slate-900 hover:bg-slate-800">{editingId ? "Save Changes" : "Add Expense"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function Field({ label, children, col }) {
  return (
    <div className={`space-y-1.5 ${col ? "col-span-2" : ""}`}>
      <Label className="text-xs text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <Card data-testid={testid} className="p-6 border-slate-200 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-display text-2xl font-semibold mt-3 font-mono-num">{value}</div>
          <div className="text-xs text-slate-500 mt-1">{hint}</div>
        </div>
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}
